/* eslint-disable @typescript-eslint/no-unused-vars */
import { iReviewFormValues } from '@services/ReviewService';
import { Field, getIn, useFormikContext } from 'formik';
import { ReactElement, useEffect, useState } from 'react';
import gridStyles from '@pages/forms/Grid.module.scss';
import { DateInput, LicenceFieldAccessible, TextInput, TileRadioAccessible, ContactInputWithExperian, EmailFieldWithExperian, Modal, ReviewAddressModal, AddressFieldAccessible } from '@components';
import { tile } from '@interfaces/interfaces';
import { reviewInfoDisplayObject } from '@services/FormFieldConfig';
import { FormType } from '@constants/forms';
import styles from './IndividualFormReview.module.scss';

function IndividualFormReview(): ReactElement {
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { values }: { values: iReviewFormValues; } = useFormikContext<iReviewFormValues>();
    const [isDisabled, setDisabled] = useState(false);
    const [circumstances, setCircumstances] = useState('');
    const pageFields = reviewInfoDisplayObject.pages.IndividualContactInformation.fields;
    const pageTitles = reviewInfoDisplayObject.pages.IndividualContactInformation;
    const [modal, setModal] = useState({
      show: false,
      title: '',
      content: <></>,
      contentClass: '',
    });
    const YesValue = (): ReactElement => (
      <>
        <div>Yes</div>
      </>
    );
    const NoValue = (): ReactElement => (
      <>
        <div>No</div>
      </>
    );

    const differentToPostal: tile[] = [
      {
        body: <YesValue />,
        value: 'true',
      },
      {
        body: <NoValue />,
        value: 'false',
      },
    ];
    const ReceiveApplicationViaEmail: tile[] = [
      {
        body: <YesValue />,
        value: 'true',
      },
      {
        body: <NoValue />,
        value: 'false',
      },
    ];

    useEffect(() => {
      if (getIn(values, 'DifferentToPostalYourDetailsIndividual')) {
          setDisabled(false);
        } else {
          setDisabled(true);
        }
    }, [getIn(values, 'DifferentToPostalYourDetailsIndividual')]);

    useEffect(() => {
      if (getIn(values, 'selectTheOption')[0] === 'SelectedGroundInfo1') {
        setCircumstances('exceptional');
      }
      if (getIn(values, 'selectTheOption')[0] === 'SelectedGroundInfo4') {
        setCircumstances('special');
      }
    }, []);

    return (
      <>
        <span className="vfH2">{pageTitles.pageTitle}</span>
        <p>{pageTitles.pageSubtitle}</p>
        <div className={gridStyles.form}>
          <div className={gridStyles.gridColFull}>
            <Field component={TextInput} name={pageFields.givenName.formikName} title={pageFields.givenName.title} isMandatory />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field component={TextInput} name={pageFields.familyName.formikName} title={pageFields.familyName.title} isMandatory />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field
              name={pageFields.dateOfBirth.formikName}
              component={DateInput}
              minDate={new Date(1900, 1, 1)}
              title={pageFields.dateOfBirth.title}
              isMandatory
              maxDate={new Date()}
              isDisabled={false}
            />
          </div>
          <div className={gridStyles.gridColFull}>
            <LicenceFieldAccessible
              title={pageFields.driverLicence.title}
              name={pageFields.driverLicence.formikName}
              nameStateOfIssue={pageFields.licenceState.formikName}
              nameCountryOfIssue={pageFields.countryOfIssue.formikName}
              nameNoDriverLicence={pageFields.noDriverLicence.formikName}
              noDriverLicenceTitle={pageFields.noDriverLicence.title}
              isMandatory
            />
          </div>
          <div className={gridStyles.gridColFull}>
            <AddressFieldAccessible name={pageFields.postalAddress.formikName} title={pageFields.postalAddress.title} manualAddressText="Enter address manually" isMandatory />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field
              component={TileRadioAccessible}
              name={pageFields.isAddressSameAsPostal.formikName}
              title={pageFields.isAddressSameAsPostal.title}
              tileData={differentToPostal}
              tileContainer
              isMandatory
              orientationRow
              classname={styles.tile}
            />
          </div>
          <div className={gridStyles.gridColFull}>
            {(getIn(values, 'DifferentToPostalYourDetailsIndividual') === 'true')
              && (
                <div className={gridStyles.gridColFull}>
                  <AddressFieldAccessible
                    name={pageFields.residentialAddress.formikName}
                    title={pageFields.residentialAddress.title}
                    manualAddressText="Enter address manually"
                    isMandatory
                    disabled={isDisabled}
                  />
                </div>
              )}
          </div>
          <Modal close={() => setModal({ ...modal, show: false })} {...modal} />
          <div>
            <button
              type="button"
              style={{ border: 'none', color: '#0072ce', backgroundColor: 'transparent', cursor: 'pointer', marginTop: '5px', textDecoration: 'underline' }}
              onClick={() => {
                  const modalTitle = 'Have you moved recently?';
                  setModal({
                    show: true,
                    title: modalTitle,
                    content: ReviewAddressModal(),
                    contentClass: '',
                  });
                }}>
              Have you moved recently?
            </button>
          </div>
          <div className={gridStyles.gridColFull}>
            <Field
              component={ContactInputWithExperian}
              name={pageFields.contactNumber.formikName}
              title="Contact phone number"
              isMandatory
              orientationRow
              obligationNumber={values.OBLIGATION_NUMBER}
              formName={FormType.REVIEW}
              secondaryContact={false}
              hasPrimaryNumberType={false}
              warningMessage="ContactNumberYourDetailsIndividualWarning"
            />
          </div>
          <div className={gridStyles.gridColFull}>
            <Field
              component={EmailFieldWithExperian}
              name={pageFields.emailAddress.formikName}
              title="Email"
              isMandatory
              orientationRow
              obligationNumber={values.OBLIGATION_NUMBER}
              formName={FormType.REVIEW}
              noEmailField={pageFields.noEmail.formikName}
              currentSP={pageFields.noEmail.title}
              warningMessage="EmailYourDetailsIndividualWarning"
            />
          </div>
          <div className={gridStyles.gridColFull}>
            {circumstances === 'special' && (getIn(values, 'YesNoEmailYourDetailsIndividual') === false)
              && (
              <Field
                component={TileRadioAccessible}
                name={pageFields.receiveApplication.formikName}
                title={pageFields.receiveApplication.title}
                tileData={ReceiveApplicationViaEmail}
                tileContainer
                isMandatory
                orientationRow
                classname={styles.tile}
              />
            )}
          </div>
          <div className={gridStyles.gridColFull}>
            {getIn(values, 'isEnforcementReviewStage') && (getIn(values, 'YesNoEmailYourDetailsIndividual') === false)
              && (
              <Field
                component={TileRadioAccessible}
                name={pageFields.receiveCorrespondence.formikName}
                title={pageFields.receiveCorrespondence.title}
                tileData={ReceiveApplicationViaEmail}
                tileContainer
                isMandatory
                orientationRow
                classname={styles.tile}
              />
            )}
          </div>
          <div className={gridStyles.gridColFull}>
            {getIn(values, 'isEnforcementReviewStage') && (<p>I understand that by providing contact details, Fines Victoria may update its records using these details and that I may be contacted regarding my outstanding fines.</p>)}
          </div>
        </div>
      </>
    );
}

export default IndividualFormReview;
